import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Pause, Play, BarChart3, Eye, MousePointer, Image as ImageIcon, Globe, MapPin, X, ArrowLeft, Upload, Check, Send, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PLACEMENTS = [
  { id: 'dashboard_banner', label: 'Dashboard Banner (728×90)' },
  { id: 'sidebar', label: 'Sidebar (300×250)' },
  { id: 'find_honey_sponsored', label: 'Find Honey — Sponsored Listing' },
  { id: 'mobile_banner', label: 'Mobile Banner (320×50)' },
];

const GEO_TARGETS = [
  { id: 'global', label: 'Global (Everywhere)' },
  { id: 'national', label: 'National (One Country)' },
  { id: 'state', label: 'State / Region' },
  { id: 'local', label: 'Local (Radius)' },
];

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-stone-100 text-stone-500',
};

const emptyForm = {
  title: '', advertiser_name: '', advertiser_email: '', advertiser_url: '', image_url: '', description: '',
  placement: 'dashboard_banner', geo_target: 'global', geo_country: '', geo_state: '',
  geo_radius_km: 50, status: 'active', start_date: new Date().toISOString().split('T')[0],
  end_date: '', weight: 1,
};

const AdminAds = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('file', file);
      const res = await fetch(`${API}/api/products/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formPayload,
      });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, image_url: `${API}/api/files/${data.path}` }));
        toast.success('Image uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const fetchAds = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/ads`, { headers });
      setAds(res.data.ads || []);
    } catch { toast.error('Failed to load ads'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAds(); }, []);

  const openCreate = () => {
    setEditingAd(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (ad) => {
    setEditingAd(ad);
    setForm({
      title: ad.title || '', advertiser_name: ad.advertiser_name || '',
      advertiser_email: ad.advertiser_email || '', advertiser_url: ad.advertiser_url || '', image_url: ad.image_url || '',
      description: ad.description || '', placement: ad.placement || 'dashboard_banner',
      geo_target: ad.geo_target || 'global', geo_country: ad.geo_country || '',
      geo_state: ad.geo_state || '', geo_radius_km: ad.geo_radius_km || 50,
      status: ad.status || 'active', start_date: ad.start_date || '',
      end_date: ad.end_date || '', weight: ad.weight || 1,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.advertiser_name.trim()) {
      toast.error('Title and advertiser name are required');
      return;
    }
    setSaving(true);
    try {
      if (editingAd) {
        await axios.put(`${API}/api/admin/ads/${editingAd.id}`, form, { headers });
        toast.success('Ad updated');
      } else {
        await axios.post(`${API}/api/admin/ads`, form, { headers });
        toast.success('Ad created');
      }
      setShowDialog(false);
      fetchAds();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save ad');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (ad) => {
    const newStatus = ad.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/api/admin/ads/${ad.id}`, { status: newStatus }, { headers });
      toast.success(`Ad ${newStatus}`);
      fetchAds();
    } catch { toast.error('Failed to update status'); }
  };

  const approveAd = async (ad) => {
    try {
      await axios.put(`${API}/api/admin/ads/${ad.id}/approve`, {}, { headers });
      toast.success('Ad approved and live!');
      fetchAds();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };

  const rejectAd = async (ad) => {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return;
    try {
      await axios.put(`${API}/api/admin/ads/${ad.id}/reject`, { reason }, { headers });
      toast.success('Ad rejected');
      fetchAds();
    } catch { toast.error('Failed to reject'); }
  };

  const sendPaymentLink = async (ad) => {
    try {
      const res = await axios.post(`${API}/api/admin/ads/${ad.id}/send-payment-link`, {}, { headers });
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send payment link'); }
  };

  const deployWithoutPayment = async (ad) => {
    if (!window.confirm(`Deploy "${ad.title}" without payment? (Collaboration/barter)`)) return;
    try {
      await axios.put(`${API}/api/admin/ads/${ad.id}/deploy`, {}, { headers });
      toast.success('Ad deployed (payment waived)');
      fetchAds();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to deploy'); }
  };

  const deleteAd = async (ad) => {
    if (!window.confirm(`Delete "${ad.title}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/ads/${ad.id}`, { headers });
      toast.success('Ad deleted');
      fetchAds();
    } catch { toast.error('Failed to delete'); }
  };

  // Summary stats
  const activeCount = ads.filter(a => a.status === 'active').length;
  const totalImpressions = ads.reduce((s, a) => s + (a.impressions || 0), 0);
  const totalClicks = ads.reduce((s, a) => s + (a.clicks || 0), 0);
  const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';

  return (
    <div className="max-w-7xl" data-testid="admin-ads-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Rocket className="h-6 w-6 text-amber-600" />
            Ad Management
          </h1>
          <p className="text-sm text-stone-500 mt-1">Create and manage supplier advertisements across your platform.</p>
        </div>
        <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="create-ad-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Ad
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{activeCount}</div>
          <div className="text-xs text-stone-500">Active Ads</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="text-2xl font-bold text-stone-800">{totalImpressions.toLocaleString()}</div>
          <div className="text-xs text-stone-500 flex items-center justify-center gap-1"><Eye className="h-3 w-3" /> Impressions</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="text-2xl font-bold text-stone-800">{totalClicks.toLocaleString()}</div>
          <div className="text-xs text-stone-500 flex items-center justify-center gap-1"><MousePointer className="h-3 w-3" /> Clicks</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="text-2xl font-bold text-stone-800">{ctr}%</div>
          <div className="text-xs text-stone-500">CTR</div>
        </div>
      </div>

      {/* Ad List */}
      {loading ? (
        <div className="text-center py-12 text-stone-400">Loading ads...</div>
      ) : ads.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
          <ImageIcon className="h-12 w-12 text-stone-200 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-stone-700">No Ads Yet</h3>
          <p className="text-sm text-stone-500 mt-1">Create your first ad to start monetizing your platform.</p>
          <Button onClick={openCreate} className="mt-4 bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="h-4 w-4 mr-2" /> Create First Ad
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {ads.map(ad => (
            <div key={ad.id} className="bg-white rounded-xl border border-stone-200 p-4 hover:border-stone-300 transition-colors" data-testid={`ad-${ad.id}`}>
              <div className="flex items-start gap-4">
                {/* Thumbnail */}
                {ad.image_url ? (
                  <>
                    <img src={ad.image_url.startsWith('http') ? ad.image_url : `${API}${ad.image_url}`} alt={ad.title} className="w-24 h-16 rounded-lg object-cover flex-shrink-0 border border-stone-100" onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
                    <div className="w-24 h-16 rounded-lg bg-stone-100 items-center justify-center flex-shrink-0 hidden"><ImageIcon className="h-6 w-6 text-stone-300" /></div>
                  </>
                ) : (
                  <div className="w-24 h-16 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="h-6 w-6 text-stone-300" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-stone-900 text-sm">{ad.title}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[ad.status] || STATUS_COLORS.expired}`}>
                      {ad.status?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs text-stone-500">{ad.advertiser_name}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-stone-400">
                    <span>{PLACEMENTS.find(p => p.id === ad.placement)?.label || ad.placement}</span>
                    <span className="flex items-center gap-1">
                      {ad.geo_target === 'global' ? <Globe className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                      {ad.geo_target === 'global' ? 'Global' : ad.geo_target === 'state' ? ad.geo_state : ad.geo_target === 'national' ? ad.geo_country : `${ad.geo_radius_km}km radius`}
                    </span>
                    <span>Weight: {ad.weight}×</span>
                    <span>{ad.start_date}{ad.end_date ? ` → ${ad.end_date}` : ' → ongoing'}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span className="text-stone-600"><Eye className="h-3 w-3 inline mr-1" />{(ad.impressions || 0).toLocaleString()} views</span>
                    <span className="text-stone-600"><MousePointer className="h-3 w-3 inline mr-1" />{(ad.clicks || 0).toLocaleString()} clicks</span>
                    <span className="text-stone-600">{ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00'}% CTR</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                  {ad.status === 'pending_review' && (
                    <>
                      <Button size="sm" onClick={() => approveAd(ad)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-3" data-testid={`approve-${ad.id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectAd(ad)} className="border-red-300 text-red-600 hover:bg-red-50 text-xs h-8 px-3" data-testid={`reject-${ad.id}`}>
                        <X className="h-3.5 w-3.5 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {ad.payment_status !== 'paid' && ad.payment_status !== 'waived' && ad.status !== 'rejected' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => sendPaymentLink(ad)} className="text-xs h-8 px-3 border-blue-300 text-blue-600 hover:bg-blue-50" title="Send Stripe payment link to supplier">
                        <Send className="h-3.5 w-3.5 mr-1" /> Send Invoice
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deployWithoutPayment(ad)} className="text-xs h-8 px-3 border-amber-300 text-amber-700 hover:bg-amber-50" title="Deploy without payment (barter/collaboration)">
                        <Rocket className="h-3.5 w-3.5 mr-1" /> Deploy Free
                      </Button>
                    </>
                  )}
                  {ad.status !== 'pending_review' && (
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(ad)} className="h-8 w-8 p-0" title={ad.status === 'active' ? 'Pause' : 'Activate'} data-testid={`toggle-${ad.id}`}>
                      {ad.status === 'active' ? <Pause className="h-4 w-4 text-yellow-600" /> : <Play className="h-4 w-4 text-green-600" />}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(ad)} className="h-8 w-8 p-0" title="Edit"><Pencil className="h-4 w-4 text-stone-500" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteAd(ad)} className="h-8 w-8 p-0" title="Delete"><Trash2 className="h-4 w-4 text-red-400" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAd ? 'Edit Ad' : 'Create New Ad'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ad Title *</Label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g., Spring Sale — 20% Off Hive Equipment" data-testid="ad-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Advertiser Name *</Label>
                <Input value={form.advertiser_name} onChange={e => setForm({...form, advertiser_name: e.target.value})} placeholder="e.g., Melbourne Bee Supplies" data-testid="ad-advertiser-input" />
              </div>
              <div>
                <Label>Advertiser Email</Label>
                <Input value={form.advertiser_email || ''} onChange={e => setForm({...form, advertiser_email: e.target.value})} placeholder="supplier@email.com" />
              </div>
            </div>
            <div>
              <Label>Link URL</Label>
              <Input value={form.advertiser_url} onChange={e => setForm({...form, advertiser_url: e.target.value})} placeholder="https://..." />
            </div>
            <div>
              <Label>Banner Image</Label>
              <div className="mt-1 space-y-2">
                <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-stone-300 rounded-xl p-4 cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <Upload className="h-5 w-5 text-stone-400" />
                  <span className="text-sm text-stone-500">{uploading ? 'Uploading...' : 'Click to upload image'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                </label>
                <div className="flex items-center gap-2 text-xs text-stone-400">
                  <span>or paste URL:</span>
                  <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." className="h-8 text-xs flex-1" />
                </div>
                {form.image_url && <img src={form.image_url.startsWith('http') ? form.image_url : `${API}${form.image_url}`} alt="Preview" className="mt-1 rounded-lg border max-h-32 object-contain" onError={e => e.target.src='data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="80" fill="%23ddd"><rect width="200" height="80" rx="8"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="12">Image not found</text></svg>'} />}
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Short description for text-only ads" className="min-h-[60px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Placement</Label>
                <Select value={form.placement} onValueChange={v => setForm({...form, placement: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLACEMENTS.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Geo Target</Label>
                <Select value={form.geo_target} onValueChange={v => setForm({...form, geo_target: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GEO_TARGETS.map(g => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.geo_target === 'national' && (
              <div>
                <Label>Country</Label>
                <Input value={form.geo_country} onChange={e => setForm({...form, geo_country: e.target.value})} placeholder="e.g., Australia" />
              </div>
            )}
            {form.geo_target === 'state' && (
              <div>
                <Label>State / Region</Label>
                <Input value={form.geo_state} onChange={e => setForm({...form, geo_state: e.target.value})} placeholder="e.g., Victoria" />
              </div>
            )}
            {form.geo_target === 'local' && (
              <div>
                <Label>Radius (km)</Label>
                <Input type="number" value={form.geo_radius_km} onChange={e => setForm({...form, geo_radius_km: parseInt(e.target.value) || 50})} />
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} placeholder="Leave blank for ongoing" />
              </div>
              <div>
                <Label>Weight (1-5)</Label>
                <Input type="number" min="1" max="5" value={form.weight} onChange={e => setForm({...form, weight: parseInt(e.target.value) || 1})} />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-ad-btn">
                {saving ? 'Saving...' : editingAd ? 'Update Ad' : 'Create Ad'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAds;
